import { usePortalMessage } from "../../../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../../../i18n/portal.js";
/**
 * "Not answering calls yet" and "Connect a number" — the design's `pc.banner` and `pc.dlg`
 * (design sync, the Claude Design export of 2026-10-09).
 *
 * THE BANNER sits at the top of the Agent page, on every tab, while the agent has no phone
 * connection (`usePhoneConnection` says `none`): "Not answering calls yet" for an agent that answers
 * calls, "Not calling yet" for a survey, with the one act that fixes it.
 *
 * THE DIALOG is that act, over the account's own SIP trunk (ADR 0029) and the number request, both of
 * which already exist:
 *
 *   · **A survey** ("Connect your phone provider") — the provider's server address, the username and
 *     password it gave you, the number people see and the countries the agent may call. "Check
 *     connection" creates the trunk with that number as caller ID, connects the number to this agent and
 *     runs the trunk's free connectivity check; a check that reaches the provider closes the dialog.
 *   · **An agent that answers calls** ("Connect a number") offers two ways:
 *       - *Connect through your phone provider*: your current number and, under Advanced, the provider's
 *         server addresses — exactly what the design draws. "Get sign-in details" creates an INBOUND-ONLY
 *         trunk (plan-015 SIP, gap G13, ADR 0046: no outbound host, username or password), connects the
 *         number to this agent and issues WeTalk's inbound sign-in details, shown ONCE with Copy, for the
 *         provider to present when it rings WeTalk. "Make a test call" then arms the trunk's test call and
 *         waits for the call to come through — that first answered call verifies the trunk.
 *       - *Ask WeTalk for a number*: a country and a note — the number request Numbers & SIP sends.
 *
 * THE PROVIDER'S ADDRESSES ARE REQUIRED. WeTalk admits a call on an inbound-only trunk only from one of them
 * AND with its sign-in details (PD8-5), so "Get sign-in details" without one opens Advanced and says so,
 * before anything is saved.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isE164, isSipTrunkInputRejection, validateSipTrunkCreate, type SipTrunkInboundOnlyInput } from "@wetalk/domain";
import { Button, Dialog, Field, Icon, Input, Select, Textarea } from "@wetalk/ui";
import { useEffect, useState, type ReactNode } from "react";

import { apiErrorText } from "../../../../api/api-error.js";
import { useGateway } from "../../../../api/provider.js";
import { QUERY_KEY } from "../../../../api/query.js";
import type {
  SipTrunkInboundAuthView,
  SipTrunkSettingsView,
  SipTrunkView,
} from "../../../../api/slice/sip-trunk.js";
import { GLYPH } from "../../../../shell/glyph.js";
import { useMemberCan } from "../../../../shell/member-capability.js";
import { NUMBER_REQUEST_COUNTRY } from "../../../numbers/number-request.js";
import { FIELD_ERROR_WORD, checkOutcomeWords, countryName } from "../../../numbers/sip-trunk-copy.js";
import { apiRefusalErrors, emptyTrunkForm, verdictOf, type TrunkFormErrors } from "../../../numbers/sip-trunk-form.js";
import { SIP_TRUNK_QUERY_KEY, sipTrunkSettingsQuery } from "../../../numbers/sip-trunk-query.js";
import { usePhoneConnection } from "./phone-connection.js";

import "./channels.css";

/** Everything that says whether a number reaches this agent — refreshed after any change here. */
export function usePhoneConnectionRefresh(voiceAgentId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: SIP_TRUNK_QUERY_KEY.sipTrunk }),
      queryClient.invalidateQueries({ queryKey: SIP_TRUNK_QUERY_KEY.sipTrunkEvent }),
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY.channelBinding, voiceAgentId] }),
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY.connection, voiceAgentId] }),
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY.concurrency, voiceAgentId] }),
      queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent }),
      queryClient.invalidateQueries({ queryKey: QUERY_KEY.number }),
      queryClient.invalidateQueries({ queryKey: QUERY_KEY.numberRequest }),
      queryClient.invalidateQueries({ queryKey: QUERY_KEY.dashboard }),
    ]);
  };
}

export interface PhoneConnectBannerProps {
  readonly voiceAgentId: string;
  /** A survey: the agent makes calls rather than answering them. */
  readonly outbound: boolean;
  readonly onConnect: () => void;
}

/** The design's `pc.banner`: drawn only while nothing connects this agent to a phone. */
export function PhoneConnectBanner({ voiceAgentId, outbound, onConnect }: PhoneConnectBannerProps): ReactNode {
  usePortalTranslation("operations");
  const phone = usePhoneConnection(voiceAgentId, outbound);
  if (phone === undefined || phone.state !== "none") return null;
  return (
    <div className="c-va-pc-banner" role="status">
      <span className="c-va-pc-banner-icon">
        <Icon glyph={GLYPH.plugs} size="2xl" />
      </span>
      <span className="c-va-pc-banner-copy">
        <span className="c-va-pc-banner-title">{outbound ? portalText("operations", "not_calling_yet_7ac1d216") : portalText("operations", "not_answering_calls_yet_d658ac24")}</span>
        <span className="c-va-pc-banner-dek">
          {outbound
            ? portalText("operations", "connect_your_phone_provider_to_start_campaigns_94ea1cca")
            : portalText("operations", "connect_a_phone_number_so_customers_can_reach_the_agent_bc81fe2d")}
        </span>
      </span>
      <Button variant="primary" onClick={onConnect}>
        {outbound ? portalText("operations", "connect_your_provider_16b60ed5") : portalText("operations", "connect_a_number_f563d967")}
      </Button>
    </div>
  );
}

/** The provider's SIP account, as both dialogs ask for it. */
interface ProviderAccount {
  readonly host: string;
  readonly username: string;
  readonly password: string;
  readonly e164: string;
  readonly allowedAddress: string;
  readonly country: readonly string[];
}

const EMPTY_ACCOUNT: ProviderAccount = { host: "", username: "", password: "", e164: "", allowedAddress: "", country: [] };

/** `+30 21 0000 0000` → `+302100000000`; anything else is left for the check to refuse. */
function e164Of(typed: string): string {
  return typed.replace(/[\s().-]/g, "");
}

function firstProblem(error: TrunkFormErrors): string {
  return Object.values(error).find((one) => one !== undefined) ?? portalText("operations", "check_the_details_and_try_again_7f48376a");
}

class AccountInvalid extends Error {
  override readonly name = "AccountInvalid";
}

/**
 * Creates the trunk from the provider's account, with the number as caller ID, and connects that
 * number to this agent. Returns the trunk and the number's id.
 */
async function connectTrunk(
  gateway: ReturnType<typeof useGateway>,
  account: ProviderAccount,
  settings: SipTrunkSettingsView | undefined,
  voiceAgentId: string,
): Promise<{ readonly trunk: SipTrunkView; readonly phoneNumberId: string }> {
  const e164 = e164Of(account.e164);
  if (!isE164(e164)) throw new AccountInvalid(portalText("operations", "write_the_number_with_its_country_code_for_example_30_21_0_0d7263ab"));
  const reviewed = settings?.destinationCountry.filter((one) => one.reviewedAt !== null).map((one) => one.code) ?? [];
  const form = {
    ...emptyTrunkForm(reviewed),
    displayName: portalText("operations", "your_phone_provider_5497760c"),
    host: account.host.trim(),
    authUsername: account.username.trim(),
    password: account.password,
    allowedAddress: account.allowedAddress,
    number: [{ key: "pc-number", phoneNumberId: null, e164, isCallerId: true }],
    allowedDestinationCountry: account.country.length > 0 ? account.country : reviewed.slice(0, 1),
  };
  const verdict = verdictOf(form, "create");
  if (verdict.kind === "invalid") throw new AccountInvalid(firstProblem(verdict.error));
  let made: SipTrunkView;
  try {
    made = await gateway.createSipTrunk(verdict.input);
  } catch (raised: unknown) {
    throw new AccountInvalid(firstProblem(apiRefusalErrors(raised, portalText("operations", "the_connection_could_not_be_saved_84ed3557"))));
  }
  const number = made.number.find((one) => one.e164 === e164) ?? made.number[0];
  if (number === undefined) throw new AccountInvalid(portalText("operations", "the_provider_s_number_was_not_saved_try_again_7dd8714f"));
  await gateway.bindSipTrunkNumber(made.sipTrunkId, number.phoneNumberId, voiceAgentId);
  return { trunk: made, phoneNumberId: number.phoneNumberId };
}

/** The provider's addresses as typed: one per line (commas too), blanks dropped. */
function addressesOf(typed: string): string[] {
  return typed
    .split(/[\n,]/)
    .map((one) => one.trim())
    .filter((one) => one !== "");
}

/**
 * plan-015 SIP (ADR 0046): creates an INBOUND-ONLY trunk for the number — the provider's addresses, no outbound
 * half, the number connected to this agent (it presents no caller ID). The settings WeTalk does not ask about are
 * Numbers & SIP's own starting values (`emptyTrunkForm`): standard SIP over UDP, HD first, numbers as +E.164.
 */
async function connectInboundOnlyTrunk(
  gateway: ReturnType<typeof useGateway>,
  account: ProviderAccount,
  voiceAgentId: string,
): Promise<{ readonly trunk: SipTrunkView; readonly phoneNumberId: string }> {
  const e164 = e164Of(account.e164);
  if (!isE164(e164)) throw new AccountInvalid(portalText("operations", "write_the_number_with_its_country_code_for_example_39_06_1_1427a9e5"));
  const allowedAddress = addressesOf(account.allowedAddress);
  if (allowedAddress.length === 0) throw new AddressMissing();
  const start = emptyTrunkForm([]);
  const body = {
    display_name: portalText("operations", "your_phone_provider_5497760c"),
    transport: start.transport,
    media_encryption: start.mediaEncryption,
    allowed_address: allowedAddress,
    wire_number_format: start.wireNumberFormat,
    national_country: null,
    caller_id_presentation: start.callerIdPresentation,
    codec_policy: start.codecPolicy,
    inbound_delivery: "direct",
    number: [{ e164, is_caller_id: false }],
  };
  // The API's own rules, run here first, so a typo is named before anything is saved.
  const checked = validateSipTrunkCreate(body);
  if (isSipTrunkInputRejection(checked)) {
    const [first] = checked.error;
    if (first?.field.startsWith("allowed_address") === true) throw new AddressMissing(FIELD_ERROR_WORD[first.code]);
    throw new AccountInvalid(first === undefined ? portalText("operations", "check_the_details_and_try_again_7f48376a") : FIELD_ERROR_WORD[first.code]);
  }
  if (checked.password !== null) throw new Error(portalText("operations", "an_inbound_only_body_validated_as_a_two_way_trunk_122f58c9"));
  const draft: SipTrunkInboundOnlyInput = checked;
  let made: SipTrunkView;
  try {
    made = await gateway.createSipTrunk(draft);
  } catch (raised: unknown) {
    throw new AccountInvalid(firstProblem(apiRefusalErrors(raised, portalText("operations", "the_connection_could_not_be_saved_84ed3557"))));
  }
  const number = made.number.find((one) => one.e164 === e164) ?? made.number[0];
  if (number === undefined) throw new AccountInvalid(portalText("operations", "your_number_was_not_saved_try_again_39074a85"));
  await gateway.bindSipTrunkNumber(made.sipTrunkId, number.phoneNumberId, voiceAgentId);
  return { trunk: made, phoneNumberId: number.phoneNumberId };
}

/** No provider address yet (or one that is not an address): Advanced opens, and says why. */
class AddressMissing extends Error {
  override readonly name = "AddressMissing";
  constructor(detail?: string) {
    super(
      detail === undefined
        ? portalText("operations", "add_your_provider_s_server_addresses_under_advanced_wetalk_3a3e4aa6")
        : portalText("operations", "check_your_provider_s_server_addresses_under_advanced_valu_9fca5cf7", { v0: String(detail) }),
    );
  }
}

export interface PhoneConnectDialogProps {
  readonly voiceAgentId: string;
  readonly outbound: boolean;
  readonly onClose: () => void;
}

export function PhoneConnectDialog({ voiceAgentId, outbound, onClose }: PhoneConnectDialogProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const settings = useQuery(sipTrunkSettingsQuery(gateway));
  const refresh = usePhoneConnectionRefresh(voiceAgentId);
  const member = useMemberCan();
  const refusal = member.whyNot("sip_trunk.manage");
  const [account, setAccount] = useState<ProviderAccount>(EMPTY_ACCOUNT);
  const [option, setOption] = useState<"provider" | "ask">("provider");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [problem, setProblem] = usePortalMessage();
  const [message, setMessage] = usePortalMessage();
  const [made, setMade] = useState<{ readonly trunk: SipTrunkView; readonly phoneNumberId: string } | null>(null);
  const [issued, setIssued] = useState<SipTrunkInboundAuthView | null>(null);
  const [waitingSince, setWaitingSince] = useState<string | null>(null);
  const [requestCountry, setRequestCountry] = useState(NUMBER_REQUEST_COUNTRY()[0]?.[0] ?? "");
  const [requestNote, setRequestNote] = useState("");

  const patch = (change: Partial<ProviderAccount>): void => {
    setAccount((held) => ({ ...held, ...change }));
  };

  const reviewed = settings.data?.destinationCountry.filter((one) => one.reviewedAt !== null) ?? [];
  const notYet = settings.data?.destinationCountry.filter((one) => one.reviewedAt === null) ?? [];
  const chosenCountry = account.country.length > 0 ? account.country : reviewed.slice(0, 1).map((one) => one.code);

  // A survey: create, connect, check. A check that reaches the provider closes the dialog.
  const check = useMutation({
    mutationFn: async () => {
      const connected = made ?? (await connectTrunk(gateway, { ...account, country: chosenCountry }, settings.data, voiceAgentId));
      setMade(connected);
      const checked = await gateway.checkSipTrunk(connected.trunk.sipTrunkId);
      return { connected, checked };
    },
    onMutate: () => {
      setProblem(null);
      setMessage(() => (portalText("operations", "placing_a_test_call_through_your_provider_fe118f44")));
    },
    onSuccess: async ({ connected, checked }) => {
      await refresh();
      const words = checkOutcomeWords(checked.outcome, connected.trunk, settings.data ?? null);
      if (words.tone === "positive") {
        onClose();
        return;
      }
      setMessage(null);
      setProblem(() => checkOutcomeWords(checked.outcome, connected.trunk, settings.data ?? null).sentence);
    },
    onError: (raised: unknown) => {
      setMessage(null);
      setProblem(() => (raised instanceof AccountInvalid ? apiErrorText(raised, "") : apiErrorText(raised, portalText("operations", "the_connection_could_not_be_checked_82c67f3d"))));
    },
  });

  // An agent that answers calls, through the provider: create the inbound-only trunk, connect, issue the sign-in details.
  const issue = useMutation({
    mutationFn: async () => {
      const connected = made ?? (await connectInboundOnlyTrunk(gateway, account, voiceAgentId));
      setMade(connected);
      const auth = await gateway.createSipTrunkInboundAuth(connected.trunk.sipTrunkId);
      return auth;
    },
    onMutate: () => {
      setProblem(null);
    },
    onSuccess: async (auth) => {
      setIssued(auth);
      await refresh();
    },
    onError: (raised: unknown) => {
      if (raised instanceof AddressMissing) setAdvancedOpen(true);
      setProblem(
        () => (raised instanceof AccountInvalid || raised instanceof AddressMissing
          ? apiErrorText(raised, "")
          : apiErrorText(raised, portalText("operations", "the_connection_could_not_be_saved_84ed3557"))),
      );
    },
  });

  const testCall = useMutation({
    mutationFn: async () => {
      if (made === null) throw new Error(portalText("operations", "get_the_sign_in_details_first_317ece1d"));
      const since = new Date().toISOString();
      await gateway.createSipTrunkTestCall(made.trunk.sipTrunkId, {
        direction: "i_call_the_agent",
        phoneNumberId: made.phoneNumberId,
      });
      return since;
    },
    onMutate: () => {
      setProblem(null);
    },
    onSuccess: (since) => {
      setWaitingSince(since);
      setMessage(() => (portalText("operations", "waiting_for_your_test_call_to_come_through_308715ba")));
    },
    onError: (raised: unknown) => {
      setProblem(() => (apiErrorText(raised, portalText("operations", "the_test_call_could_not_be_armed_842d26fd"))));
    },
  });

  // While a test call is armed, look for it every two seconds; one that arrives closes the dialog.
  useEffect(() => {
    if (waitingSince === null || made === null) return undefined;
    const timer = window.setInterval(() => {
      void gateway.readSipTrunkTestCall(made.trunk.sipTrunkId, made.phoneNumberId, waitingSince).then(
        (progress) => {
          if (progress.state === "in_progress" || progress.state === "ended") {
            window.clearInterval(timer);
            setWaitingSince(null);
            setMessage(() => (portalText("operations", "your_test_call_came_through_the_agent_answers_this_number_155f3d9e")));
            void refresh();
          }
        },
        () => {
          // Not readable yet: the next tick asks again.
        },
      );
    }, 2_000);
    return () => {
      window.clearInterval(timer);
    };
  }, [gateway, made, waitingSince, refresh, setMessage]);

  const request = useMutation({
    mutationFn: () => gateway.requestPhoneNumber({ voiceAgentId, country: requestCountry, note: requestNote }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
    onError: (raised: unknown) => {
      setProblem(() => (apiErrorText(raised, portalText("operations", "the_request_could_not_be_sent_nothing_has_changed_4512162e"))));
    },
  });

  const pending = check.isPending || issue.isPending || testCall.isPending || request.isPending;

  const primary = outbound
    ? { label: check.isPending ? portalText("operations", "checking_820d6004") : portalText("operations", "check_connection_14caeff0"), act: () => check.mutate() }
    : option === "ask"
      ? { label: request.isPending ? portalText("operations", "sending_cf765512") : portalText("operations", "request_a_number_705e332d"), act: () => request.mutate() }
      : issued === null
        ? { label: issue.isPending ? portalText("operations", "saving_56a2285c") : portalText("operations", "get_sign_in_details_73de6e0c"), act: () => issue.mutate() }
        : { label: waitingSince !== null || testCall.isPending ? portalText("operations", "calling_acd4a67d") : portalText("operations", "make_a_test_call_0e86ae7f"), act: () => testCall.mutate() };

  return (
    <Dialog
      open
      title={outbound ? portalText("operations", "connect_your_phone_provider_ccda69c7") : portalText("operations", "connect_a_number_f563d967")}
      onClose={onClose}
      // The sign-in details are shown once: only the dialog's own button may close it then.
      dismissible={issued === null}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {portalText("operations", "close_bbfa773e")}</Button>
          {refusal === null ? (
            <Button variant="primary" disabled={pending || waitingSince !== null} onClick={primary.act}>
              {primary.label}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="c-va-pc-dialog">
        {refusal === null ? null : <p className="c-note">{refusal}</p>}

        {outbound ? (
          <>
            <ProviderAccountFields account={account} onChange={patch} />
            <Field label={portalText("operations", "number_people_see_adbb1da7")}>
              {(control) => (
                <Input
                  {...control}
                  inputMode="tel"
                  placeholder="+30 21 0000 0000"
                  value={account.e164}
                  onChange={(event) => {
                    patch({ e164: event.currentTarget.value });
                  }}
                />
              )}
            </Field>
            <div className="field">
              <span className="c-va-pc-label">{portalText("operations", "countries_the_agent_may_call_f24d9fdf")}</span>
              <span className="c-va-pc-chips">
                {reviewed.map((one) => {
                  const on = chosenCountry.includes(one.code);
                  return (
                    <button
                      type="button"
                      key={one.code}
                      className="c-va-pc-chip"
                      aria-pressed={on}
                      onClick={() => {
                        patch({
                          country: on ? chosenCountry.filter((code) => code !== one.code) : [...chosenCountry, one.code],
                        });
                      }}
                    >
                      <Icon glyph={on ? GLYPH.check : GLYPH.plus} size="xs" />
                      {countryName(one.code)}
                    </button>
                  );
                })}
              </span>
              {notYet.length === 0 ? null : (
                <span className="c-va-pc-hint">
                  {notYet.map((one) => countryName(one.code)).join(", ")} {notYet.length === 1 ? portalText("operations", "isn_t_ee2ebf51") : portalText("operations", "aren_t_624cd1f4")} {portalText("operations", "available_yet_3e3b9e03")}</span>
              )}
            </div>
          </>
        ) : (
          <>
            <div role="radiogroup" aria-label={portalText("operations", "how_to_connect_64ec8dc5")} className="c-va-pc-options">
              {(
                [
                  [
                    "provider",
                    portalText("operations", "connect_through_your_phone_provider_f97d4172"),
                    portalText("operations", "use_a_number_you_already_have_your_provider_sends_its_call_9af59ac6"),
                  ],
                  ["ask", portalText("operations", "ask_wetalk_for_a_number_295a5ffb"), portalText("operations", "tell_us_the_country_we_set_it_up_and_it_appears_here_once_5cbd919d")],
                ] as const
              ).map(([id, label, dek]) => (
                <label key={id} className="c-va-pc-option" data-on={option === id ? "true" : "false"}>
                  <input
                    type="radio"
                    name="pcOpt"
                    checked={option === id}
                    disabled={issued !== null}
                    onChange={() => {
                      setOption(id);
                      setProblem(null);
                    }}
                  />
                  <span>
                    <span className="c-va-pc-option-label">{label}</span>
                    <span className="c-va-pc-option-dek">{dek}</span>
                  </span>
                </label>
              ))}
            </div>

            {option === "provider" ? (
              <>
                <Field label={portalText("operations", "your_current_number_8afbeca7")}>
                  {(control) => (
                    <Input
                      {...control}
                      inputMode="tel"
                      placeholder="+39 06 1234 5678"
                      value={account.e164}
                      disabled={issued !== null}
                      onChange={(event) => {
                        patch({ e164: event.currentTarget.value });
                      }}
                    />
                  )}
                </Field>
                {issued === null ? null : (
                  <>
                    <p className="c-va-pc-copy-note">
                      {portalText("operations", "copy_these_sign_in_details_now_and_enter_them_at_your_phon_80e22dbd")}</p>
                    <dl className="c-va-pc-creds">
                      <SignInDetailRow term={portalText("operations", "address_d70f93df")} value={settings.data?.sipEdgeHost ?? "—"} />
                      <SignInDetailRow term={portalText("operations", "username_84c29015")} value={issued.inboundAuthUsername} />
                      <SignInDetailRow term={portalText("operations", "password_8be3c943")} value={issued.inboundPassword} />
                    </dl>
                  </>
                )}
                <div className="c-va-pc-advanced">
                  <button
                    type="button"
                    className="c-va-pc-advanced-toggle"
                    aria-expanded={advancedOpen}
                    onClick={() => {
                      setAdvancedOpen(!advancedOpen);
                    }}
                  >
                    <Icon glyph={advancedOpen ? GLYPH.caretDown : GLYPH.caretRight} size="xs" />
                    {portalText("operations", "advanced_connection_details_sip_5ab199e2")}</button>
                  {advancedOpen ? (
                    <div className="c-va-pc-advanced-body c-va-mono-field">
                      <Field
                        label={portalText("operations", "your_provider_s_server_addresses_933c425c")}
                        description={portalText("operations", "ip_addresses_or_ranges_only_one_per_line_446b9e95")}
                      >
                        {(control) => (
                          <Textarea
                            {...control}
                            rows={2}
                            placeholder={"203.0.113.10\n203.0.113.0/28"}
                            value={account.allowedAddress}
                            disabled={issued !== null}
                            onChange={(event) => {
                              patch({ allowedAddress: event.currentTarget.value });
                            }}
                          />
                        )}
                      </Field>
                    </div>
                  ) : null}
                </div>
              </>
            ) : (
              <>
                <Field label={portalText("operations", "country_d523ebbd")}>
                  {(control) => (
                    <Select
                      {...control}
                      value={requestCountry}
                      onChange={(event) => {
                        setRequestCountry(event.currentTarget.value);
                      }}
                    >
                      {NUMBER_REQUEST_COUNTRY().map(([code, name]) => (
                        <option key={code} value={code}>
                          {name}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label={portalText("operations", "note_for_wetalk_optional_3228fdc1")}>
                  {(control) => (
                    <Textarea
                      {...control}
                      rows={2}
                      placeholder={portalText("operations", "for_example_a_rome_number_if_possible_21429557")}
                      value={requestNote}
                      onChange={(event) => {
                        setRequestNote(event.currentTarget.value);
                      }}
                    />
                  )}
                </Field>
                <p className="c-va-pc-hint">{portalText("operations", "setting_up_a_number_usually_takes_a_few_days_b93d6fa1")}</p>
              </>
            )}
          </>
        )}

        {message === null ? null : (
          <span role="status" className="c-va-pc-message">
            {message}
          </span>
        )}
        {problem === null ? null : (
          <p className="c-note c-note-critical" role="alert">
            {problem}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function ProviderAccountFields({
  account,
  onChange,
}: {
  readonly account: ProviderAccount;
  readonly onChange: (change: Partial<ProviderAccount>) => void;
}): ReactNode {
  usePortalTranslation("operations");
  return (
    <>
      <span className="c-va-mono-field">
      <Field label={portalText("operations", "your_provider_s_server_address_09c2e269")}>
        {(control) => (
          <Input
            {...control}
            placeholder="sip.provider.example"
            value={account.host}
            onChange={(event) => {
              onChange({ host: event.currentTarget.value });
            }}
          />
        )}
      </Field>
      </span>
      <div className="c-va-pc-pair">
        <Field label={portalText("operations", "username_84c29015")}>
          {(control) => (
            <Input
              {...control}
              autoComplete="off"
              value={account.username}
              onChange={(event) => {
                onChange({ username: event.currentTarget.value });
              }}
            />
          )}
        </Field>
        <Field label={portalText("operations", "password_8be3c943")}>
          {(control) => (
            <Input
              {...control}
              type="password"
              autoComplete="new-password"
              value={account.password}
              onChange={(event) => {
                onChange({ password: event.currentTarget.value });
              }}
            />
          )}
        </Field>
      </div>
    </>
  );
}

/** One line of the sign-in details: the term, the value in mono, and Copy. */
export function SignInDetailRow({ term, value }: { readonly term: string; readonly value: string }): ReactNode {
  usePortalTranslation("operations");
  const [copied, setCopied] = useState(false);
  return (
    <>
      <dt>{term}</dt>
      <dd className="c-va-mono">{value}</dd>
      <Button
        variant="ghost"
        onClick={() => {
          const clipboard = globalThis.navigator.clipboard as Clipboard | undefined;
          void clipboard?.writeText(value).then(() => {
            setCopied(true);
          });
        }}
      >
        {copied ? portalText("operations", "copied_8e3df45a") : portalText("operations", "copy_af74f7c5")}
      </Button>
    </>
  );
}
